import React, { createContext } from 'react';
import { EventCreator, Event, Type, State, useUndoRedo } from '../hooks/useUndoRedo';

type UndoRedoCreator = (prevState: State, state: State) => void;

export enum UndoRedoActions {
  undo = 'undo',
  redo = 'redo',
}

type Dispatch = {
  events: Event[];
  undoneEvents: Event[];
  isEventsNotEmpty: boolean;
  isUndoneEventsNotEmpty: boolean;
  undo: () => void;
  redo: () => void;
  registerUndoRedoCreator: (type: Type, eventCreator: EventCreator) => UndoRedoCreator;
  clearEvents: () => void;
  getLastEventName: (action: UndoRedoActions) => string;
};

export const UndoRedoContext = createContext<Dispatch>({
  events: [],
  undoneEvents: [],
  isEventsNotEmpty: false,
  isUndoneEventsNotEmpty: false,
  undo: () => null,
  redo: () => null,
  registerUndoRedoCreator: () => () => null,
  clearEvents: () => null,
  getLastEventName: () => '',
});

export const UndoRedoProvider: React.FC = ({ children }): JSX.Element => {
  const {
    events,
    undoneEvents,
    isEventsNotEmpty,
    isUndoneEventsNotEmpty,
    saveEvent,
    undoEvent,
    redoEvent,
    clearEvents,
  } = useUndoRedo();

  const registerUndoRedoCreator = (type: Type, eventCreator: EventCreator): UndoRedoCreator => {
    return (prevState: State, state: State) => {
      saveEvent(type, prevState, state, eventCreator);
    };
  };

  const undo = (): void => {
    const event = undoEvent();

    if (event) {
      const { prevState, creator } = event;
      creator(prevState);
    }
  };

  const redo = (): void => {
    const event = redoEvent();

    if (event) {
      const { state, creator } = event;
      creator(state);
    }
  };

  const getLastEventName = (action: UndoRedoActions): string => {
    const lastUndoneEvent = undoneEvents.slice(-1);
    const lastEvent = events.slice(-1);
    if (action === UndoRedoActions.undo) {
      return lastEvent.length ? (lastEvent[0].type as string) : (lastUndoneEvent[0].type as string);
    }

    return lastUndoneEvent[0].type as string;
  };

  return (
    <UndoRedoContext.Provider
      value={{
        events,
        undoneEvents,
        isEventsNotEmpty,
        isUndoneEventsNotEmpty,
        undo,
        redo,
        registerUndoRedoCreator,
        clearEvents,
        getLastEventName,
      }}
    >
      {children}
    </UndoRedoContext.Provider>
  );
};
